import React from 'react';
import { motion } from 'framer-motion';
import CTAPair from '@/components/site/CTAPair';
import { HERO } from '@/constants/testIds';

export default function HomeHero() {
  return (
    <section className="relative overflow-hidden bg-[#000919] text-white">
      {/* Subtle radial — much cleaner than the previous oversized blobs */}
      <div
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          background:
            'radial-gradient(900px 500px at 88% 12%, rgba(138,1,230,0.22), transparent 60%), radial-gradient(700px 400px at 10% 100%, rgba(255,80,129,0.14), transparent 60%)',
        }}
      />
      <div className="pointer-events-none absolute inset-0 bg-grain opacity-40" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-24 md:pt-28 md:pb-32 lg:pt-32 lg:pb-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-xs font-medium text-white/70"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF5081]" />
              Fully managed media · open-web reach
            </motion.div>

            <motion.h1
              data-testid={HERO.headline}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-7 font-display text-[44px] leading-[1.02] sm:text-5xl lg:text-6xl xl:text-[68px] font-semibold tracking-[-0.025em] text-white"
            >
              Your media, managed.
              <br />
              Your results, <span className="text-[#FF5081]">owned.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-7 max-w-2xl text-base lg:text-lg text-white/65 leading-relaxed"
            >
              Sellrly is a fully managed media service company for DTC brands, podcasters, creators, social sellers,
              and subscription businesses. We put your spend where it performs — across the open web, not just inside
              Meta and Google — and we run the whole thing for you.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.18 }}
              className="mt-9"
            >
              <CTAPair testIdSuffix="hero" secondaryTarget="#how-it-works" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-14 pt-8 border-t border-white/10 flex flex-wrap items-center gap-x-8 gap-y-3 text-[11px] uppercase tracking-[0.22em] text-white/45"
            >
              <span>DTC &amp; ecommerce</span>
              <span className="w-1 h-1 rounded-full bg-white/25" />
              <span>Podcasts</span>
              <span className="w-1 h-1 rounded-full bg-white/25" />
              <span>Creators</span>
              <span className="w-1 h-1 rounded-full bg-white/25" />
              <span>Social commerce</span>
              <span className="w-1 h-1 rounded-full bg-white/25" />
              <span>Subscriptions</span>
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="relative"
            >
              {/* On-brand Sellrly illustration from current site */}
              <div className="relative aspect-square max-w-[560px] ml-auto">
                <div
                  className="absolute inset-0 -m-8 rounded-full opacity-60"
                  style={{
                    background:
                      'radial-gradient(circle at 50% 50%, rgba(255,80,129,0.18), transparent 65%)',
                  }}
                />
                <img
                  src="/brand/sellrly-hero.webp"
                  alt="Sellrly — fully managed media"
                  className="relative w-full h-full object-contain drop-shadow-[0_25px_80px_rgba(138,1,230,0.35)]"
                />
              </div>

              {/* Stat tile floating */}
              <div className="absolute -bottom-4 left-2 sm:left-0 rounded-2xl p-5 bg-[#0E103D]/95 border border-white/10 backdrop-blur-sm w-[200px] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.7)]">
                <p className="text-[10px] uppercase tracking-[0.22em] text-white/45">Featured result</p>
                <p className="mt-2 font-display text-2xl font-semibold text-white">
                  <span className="text-[#FF5081]">61%</span> below
                </p>
                <p className="text-[11px] text-white/55 mt-1">target CPA · 14 days</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
